// ─────────────────────────────────────────────────────────────────
// List block — canvas renderer
// ----------------------------------------------------------------
// The list block repeats one item template once per item of the list variable
// it is bound to. The item template is a single block (attrs.itemBlock), so the
// author's row is that block — a Section or Columns when the item needs more
// than one part — and the canvas edits it like any other block, with the item's
// fields in scope.
//
// What the canvas shows is the *template*, not the items: merge fields stay as
// tokens to be edited. The sample item (the list's `default[0]`) is what the
// template's own defaults preview with, which is the same value a Preview build
// renders, and the fields it carries are what becomes highlightable inside.
// ─────────────────────────────────────────────────────────────────
import type { Block, ListBlock } from '@blogforge/shared';
import type { DragPath } from '../../types';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { BlockWrapper } from '../BlockWrapper';
import { BlockAdder } from '../BlockAdder';
import { ListChecks } from 'lucide-react';
import {
  itemScopeDefinitions,
  listItemNames,
  listSampleItem,
  resolveListBinding,
} from '../../lib/list-variables.js';

interface ListRendererProps {
  block: ListBlock;
  path: DragPath;
  rawVariableDefinitions: Record<string, unknown> | undefined;
  /** Workspace audience attribute keys, when the document may merge them. */
  audienceAttributeKeys?: readonly string[] | undefined;
}

/** The block inside the slot, when there is one. */
function itemTemplate(block: ListBlock): Block | null {
  const template = block.attrs?.itemBlock;
  return template && typeof template === 'object' ? (template as Block) : null;
}

export function ListRenderer({
  block,
  path,
  rawVariableDefinitions,
  audienceAttributeKeys,
}: ListRendererProps) {
  const binding = resolveListBinding(rawVariableDefinitions, block.attrs?.dataBindingKey ?? '');
  const template = itemTemplate(block);

  if (!binding.ok) {
    // A broken binding is a normal state while the author picks a variable, so
    // it is a card that says what is wrong rather than an error.
    return (
      <div className="border-danger/40 bg-danger/5 rounded-lg border border-dashed p-3">
        <p className="text-danger flex items-center gap-1.5 text-xs font-semibold">
          <ListChecks size={13} />
          List not bound
        </p>
        <p className="text-ink-soft mt-1 text-xs">{binding.problem}</p>
        <p className="text-muted mt-1 text-[11px]">
          Choose the list variable this block repeats in the inspector.
        </p>
      </div>
    );
  }

  const { definition } = binding;
  const fields = listItemNames(definition);
  const sample = listSampleItem(definition);
  const itemPath: DragPath = [...path, { id: block.id, type: 'list' }];
  // Everything inside the item template sees the document's variables *plus*
  // this item's fields, with the fields shadowing same-named ones — which is
  // what makes `{{title}}` mean the item's title in here. Passing the scope as
  // the subtree's definitions is what keeps the pickers, the default previews
  // and the highlightable names on the one set.
  const itemScope = itemScopeDefinitions(rawVariableDefinitions, definition);

  return (
    <div className="border-line relative rounded-lg border border-dashed p-3">
      <div className="mb-2 flex flex-wrap items-center gap-x-2 gap-y-1">
        <p className="text-muted text-[11px] font-semibold tracking-[0.12em] uppercase">
          List
        </p>
        <code className="text-ink-soft font-mono text-[11px]">{`{{${block.attrs.dataBindingKey}}}`}</code>
        <span className="text-muted text-[11px]">
          repeats for every item — the sample item is shown
        </span>
      </div>

      {fields.length > 0 && (
        <div className="mb-2 flex flex-wrap gap-1">
          {fields.map((field) => {
            const value = sample?.[field];
            return (
              <span
                key={field}
                className="border-line bg-bg-soft text-muted rounded border px-1.5 py-0.5 font-mono text-[10px]"
                title={value === undefined ? 'No sample value' : `Sample value: ${String(value)}`}
              >
                {field}
                {value !== undefined && (
                  <span className="text-ink-soft"> = {String(value)}</span>
                )}
              </span>
            );
          })}
        </div>
      )}

      {template ? (
        <>
          <SortableContext items={[template.id]} strategy={verticalListSortingStrategy}>
            <BlockWrapper
              block={template}
              path={itemPath}
              rawVariableDefinitions={itemScope}
              audienceAttributeKeys={audienceAttributeKeys}
              soleBlock
            />
          </SortableContext>
          {block.attrs?.dividerType === 'line' && (
            <div className="mt-3">
              <div
                className="h-px w-full"
                style={{ backgroundColor: block.attrs.dividerColor || '#E5E7EB' }}
              />
              <p className="text-muted mt-1 text-[10px]">Rule between items</p>
            </div>
          )}
        </>
      ) : (
        <div className="border-line/70 bg-bg-soft/30 rounded-lg border border-dashed p-3">
          <p className="text-muted text-center text-xs">
            No item template yet — every item renders this one block.
          </p>
          <p className="text-muted mt-1 text-center text-[11px]">
            Need several parts per item (image, title, price)? Add a Section or Columns block here
            and build the row inside it.
          </p>
          <BlockAdder parent={{ id: block.id, type: 'list' }} path={path} />
        </div>
      )}
    </div>
  );
}
